<template>
  <div>
    <div v-if="!isMyBritaLoggedIn" class="mb-12 flex flex-col gap-2">
      <CommonLabel path="BWF/shop/checkout/addressForm/alreadyRegisteredCTA" class="font-medium" />
      <button class="text-link flex" @click="loginMsal()">
        <CommonLabel path="BWF/shop/shared/loginNow" />
      </button>
    </div>

    <form @submit.prevent="submitPersonalData()">
      <div class="flex flex-col gap-8 md:gap-12">
        <div class="grid gap-4 xl:w-7/12 xl:grid-cols-2 xl:gap-6">
          <CommonLabel
            path="BWF/shop/checkout/addressForm/personals"
            class="col-start-1 font-medium"
          />

          <SelectField
            v-model="personalDataState.salutation"
            class="form-field-grey col-start-1"
            name="salutationId"
            required="required"
            :label="personalInformationLabels?.chooseSalutationLabel"
            :options="selectableSalutations"
            :error-message="personalFormVuelidate?.salutation?.$errors?.[0]?.$message"
            @blur="personalFormVuelidate?.salutation?.$touch()"
          />

          <InputField
            v-model="personalDataState.firstName"
            class="form-field-grey col-start-1"
            name="first-name"
            required="required"
            :placeholder="personalInformationLabels?.firstNamePlaceholder"
            :label="personalInformationLabels?.firstNameLabel"
            :error-message="personalFormVuelidate?.firstName?.$errors?.[0]?.$message"
            @blur="personalFormVuelidate?.firstName?.$touch()"
          />
          <InputField
            v-model="personalDataState.lastName"
            class="form-field-grey"
            name="last-name"
            required="required"
            :placeholder="personalInformationLabels?.lastNamePlaceholder"
            :label="personalInformationLabels?.lastNameLabel"
            :error-message="personalFormVuelidate?.lastName?.$errors?.[0]?.$message"
            @blur="personalFormVuelidate?.lastName?.$touch()"
          />
          <InputField
            v-model="personalDataState.email"
            class="form-field-grey col-start-1"
            name="email-address"
            required="required"
            :placeholder="personalInformationLabels?.eMailAddressPlaceholder"
            :label="personalInformationLabels?.eMailAddressLabel"
            :error-message="personalFormVuelidate?.email?.$errors?.[0]?.$message"
            :disabled="azureUser.username"
            @blur="
              personalFormVuelidate?.email?.$touch();
              !personalFormVuelidate?.email?.$errors?.[0]?.$message
                ? newsletterDoiCheck(personalDataState.email)
                : {};
            "
          />

          <CommonLabel path="BWF/shop/shared/address" class="col-start-1 font-medium" />

          <InputField
            v-model="personalDataState.street"
            class="form-field-grey col-start-1"
            name="street-address"
            :placeholder="personalInformationLabels?.streetAndHouseNoPlaceholder"
            :label="personalInformationLabels?.streetAndHouseNoLabel"
            :error-message="personalFormVuelidate?.street?.$errors?.[0]?.$message"
            @blur="personalFormVuelidate?.street?.$touch()"
          />

          <InputField
            v-model="personalDataState.additionalAddressLine1"
            class="form-field-grey col-start-1"
            name="additionalAddressLine1"
            :placeholder="personalInformationLabels?.addressSuffixPlaceholder"
            :label="personalInformationLabels?.addressSuffixLabel"
            :error-message="personalFormVuelidate?.additionalAddressLine1?.$errors?.[0]?.$message"
            @blur="personalFormVuelidate?.additionalAddressLine1?.$touch()"
          />

          <InputField
            v-model="personalDataState.postalCode"
            class="form-field-grey col-start-1"
            name="postal-code"
            :placeholder="personalInformationLabels?.postalCodePlaceholder"
            :label="personalInformationLabels?.postalCodeLabel"
            :error-message="personalFormVuelidate?.postalCode?.$errors?.[0]?.$message"
            @blur="personalFormVuelidate?.postalCode?.$touch()"
          />

          <InputField
            v-model="personalDataState.city"
            class="form-field-grey"
            name="city"
            :placeholder="personalInformationLabels?.cityPlaceholder"
            :label="personalInformationLabels?.cityLabel"
            :error-message="personalFormVuelidate?.city?.$errors?.[0]?.$message"
            @blur="personalFormVuelidate?.city?.$touch()"
          />

          <SelectField
            v-model="personalDataState.country"
            class="form-field-grey col-start-1"
            name="country"
            :disabled="true"
            :label="personalInformationLabels?.countryLabel"
            :options="selectableCountries"
          />
        </div>

        <div class="prose prose-sm prose-black max-w-none hyphens-auto">
          <CommonLabel path="/BWF/form/shared/requiredFieldLabel" />
          <div class="mt-2">
            <CommonLabel path="/BWF/form/shared/privacyLinkLabel" />
          </div>
        </div>

        <slot></slot>
        <button class="btn btn-blue w-full self-start md:w-auto" type="submit">
          <CommonLabel path="BWF/shop/shared/continue" />
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import useProductRegistrationState from "~/composables/shop/useProductRegistrationState";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import InputField from "~/templates/elements/form/InputField.vue";
import SelectField from "~/templates/elements/form/SelectField.vue";
import { useVuelidate } from "@vuelidate/core";
import useProductRegistrationPersonalInformation from "~/composables/shop/useProductRegistrationPersonalInformation";
import { useProductRegistrationNewsletter } from "~/composables/shop/useProductRegistrationNewsletter";

const { $msal } = useNuxtApp();
const loginMsal = () => {
  $msal.signIn();
};

const { verifyLogIn, isMyBritaLoggedIn } = useMyBritaUser();
const { azureUser, isAzureAuthenticated } = useAzureUser();
const { userDefaultBillingAddress } = useUser();
const emit = defineEmits(["submit-personal-data"]);
const { refreshSessionContext } = useSessionContext();
const { newsletterDoiCheck } = useProductRegistrationNewsletter();

const {
  personalInformationLabels,
  selectableCountries,
  selectableSalutations,
  productRegistrationPersonalInformationRules,
} = await useProductRegistrationPersonalInformation();

const { addMultipleValues, registrationState } = await useProductRegistrationState();

const personalDataState = reactive({
  salutation: "",
  firstName: "",
  lastName: "",
  email: "",
  street: "",
  additionalAddressLine1: "",
  postalCode: "",
  city: "",
  country: selectableCountries?.[0]?.value ?? "",
});

const selectedSalutation = computed(() => {
  return selectableSalutations.find(
    (salutation) => salutation?.value === personalDataState?.salutation,
  )?.value;
});

const personalFormVuelidate = useVuelidate(
  productRegistrationPersonalInformationRules,
  personalDataState,
);

const submitPersonalData = async () => {
  personalFormVuelidate.value.$touch();
  try {
    if (await personalFormVuelidate.value.$validate()) {
      addMultipleValues({
        bri_salutation_opt: selectedSalutation.value,
        bri_firstname: personalDataState.firstName,
        bri_lastname: personalDataState.lastName,
        bri_email: personalDataState.email,
        bri_street1: personalDataState.street,
        bri_additional_address_line_1: personalDataState.additionalAddressLine1,
        bri_postalcode: personalDataState.postalCode,
        bri_city: personalDataState.city,
        bri_country: personalDataState.country,
      });
      emit("submit-personal-data");
    }
  } catch (e) {
    console.error("Error submitting personal data:", e);
  }
};

onMounted(async () => {
  if (isAzureAuthenticated.value) {
    await verifyLogIn();
  }
  personalDataState.salutation =
    userDefaultBillingAddress.value?.salutation?.salutationKey ??
    registrationState?.value.bri_salutation_opt;
  personalDataState.firstName =
    registrationState.value?.bri_firstname !== ""
      ? registrationState.value?.bri_firstname
      : userDefaultBillingAddress?.value?.firstName !== ""
        ? userDefaultBillingAddress?.value?.firstName
        : azureUser.value?.idTokenClaims?.["given_name"];
  personalDataState.lastName =
    registrationState.value?.bri_lastname !== ""
      ? registrationState.value?.bri_lastname
      : userDefaultBillingAddress?.value?.lastName !== ""
        ? userDefaultBillingAddress?.value?.lastName
        : azureUser.value?.idTokenClaims?.["family_name"];
  personalDataState.email = azureUser.value?.username ?? registrationState.value.bri_email;
  personalDataState.street =
    userDefaultBillingAddress.value?.street ?? registrationState.value.bri_street1;
  personalDataState.additionalAddressLine1 =
    userDefaultBillingAddress.value?.additionalAddressLine1 ??
    registrationState.value.bri_additional_address_line_1;
  personalDataState.postalCode =
    userDefaultBillingAddress.value?.zipcode ?? registrationState.value.bri_postalcode;
  personalDataState.city =
    userDefaultBillingAddress.value?.city ?? registrationState.value.bri_city;

  if (isMyBritaLoggedIn.value) {
    if (!userDefaultBillingAddress.value) {
      await refreshSessionContext();
    }
    await submitPersonalData();
  }

  window.scrollTo(0, 0);
});
</script>
